import { useEffect, useRef } from 'react'

// How often the title swaps between the alert message and the page's real title, in ms.
const FLASH_INTERVAL_MS = 1000

// Brand red used for the favicon badge (matches --color-brand in index.css).
const BADGE_COLOR = '#f02d5e'

function getFaviconLink(): HTMLLinkElement | null {
  if (typeof document === 'undefined') return null
  return document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
}

// Draw a small "alert" favicon — a filled brand-colored circle with a white check — and return it as
// a PNG data URL. Returns null if the canvas API is unavailable, so callers can skip the badge.
function makeAlertFavicon(): string | null {
  try {
    const size = 32
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.fillStyle = BADGE_COLOR
    ctx.beginPath()
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 3.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(9, 16.5)
    ctx.lineTo(14, 21.5)
    ctx.lineTo(23, 10.5)
    ctx.stroke()
    return canvas.toDataURL('image/png')
  } catch {
    return null
  }
}

/**
 * Dynamic Tab Alert.
 *
 * When `active` transitions to `true` *while the browser tab is hidden*, grab the user's attention by
 * flashing `message` in the tab title (alternating with the page's real title) and badging the
 * favicon. The alert clears itself — restoring the original title and favicon — the instant the user
 * refocuses the tab. Arming only while the tab is hidden means a user who is already looking at the
 * page is never interrupted; what they see stays untouched.
 *
 * Re-arming only happens on a fresh `false -> true` transition of `active`, so a condition that stays
 * true (e.g. "analysis is complete") fires the alert exactly once.
 */
export function useTabAlert(active: boolean, message: string): void {
  // Track the latest message without making it an effect dependency, so a late-arriving value (e.g. a
  // project name that loads after completion) doesn't restart the effect and drop an in-flight alert.
  const messageRef = useRef(message)
  messageRef.current = message
  const prevActiveRef = useRef(active)

  useEffect(() => {
    const wasActive = prevActiveRef.current
    prevActiveRef.current = active

    // Only act on a fresh transition into `active`, and only when the user isn't already looking.
    if (!active || wasActive) return
    if (typeof document === 'undefined' || !document.hidden) return

    const alertText = messageRef.current
    const originalTitle = document.title
    const faviconLink = getFaviconLink()
    const originalFaviconHref = faviconLink?.getAttribute('href') ?? null
    const originalFaviconType = faviconLink?.getAttribute('type') ?? null
    const alertFavicon = makeAlertFavicon()

    if (faviconLink && alertFavicon) {
      faviconLink.setAttribute('type', 'image/png')
      faviconLink.setAttribute('href', alertFavicon)
    }

    let showingAlert = true
    document.title = alertText
    const flash = window.setInterval(() => {
      showingAlert = !showingAlert
      document.title = showingAlert ? alertText : originalTitle
    }, FLASH_INTERVAL_MS)

    let cleared = false
    const restore = () => {
      if (cleared) return
      cleared = true
      window.clearInterval(flash)
      document.title = originalTitle
      if (faviconLink) {
        if (originalFaviconHref !== null) faviconLink.setAttribute('href', originalFaviconHref)
        if (originalFaviconType !== null) faviconLink.setAttribute('type', originalFaviconType)
      }
      document.removeEventListener('visibilitychange', onVisibility)
    }
    // Clear the moment the user comes back to the tab.
    const onVisibility = () => {
      if (!document.hidden) restore()
    }
    document.addEventListener('visibilitychange', onVisibility)

    // Also restore if the component unmounts or `active` changes before the user returns.
    return restore
  }, [active])
}
